import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { Branch } from '@dto/Branch';
import { BranchApiService } from './branch-api.service';

/**
 * With fewer than two branches the selection is neither shown nor applied: requests go without
 * branchId and the backend falls back to the main branch.
 */
@Injectable({ providedIn: 'root' })
export class BranchContext {
  private static readonly STORAGE_KEY = 'selectedBranchId';

  private readonly branchesSubject = new BehaviorSubject<Branch[]>([]);
  readonly branches$ = this.branchesSubject.asObservable();

  // Read synchronously: services injected into the HTTP interceptor are created on the first
  // request, which must already carry branchId before the branch list loads.
  private currentId: number | undefined = this.readStored();

  private loaded = false;

  constructor(private readonly api: BranchApiService) {}

  get branches(): Branch[] {
    return this.branchesSubject.value;
  }

  get hasMultiple(): boolean {
    return this.branchesSubject.value.length >= 2;
  }

  get mainBranchId(): number | undefined {
    return (this.branches.find(b => b.isMain) ?? this.branches[0])?.id;
  }

  get currentBranchId(): number | undefined {
    if (!this.loaded) {
      return this.currentId;
    }
    if (!this.hasMultiple) {
      return undefined;
    }
    return this.currentId && this.branches.some(b => b.id === this.currentId) ? this.currentId : this.mainBranchId;
  }

  get currentBranch(): Branch | undefined {
    return this.branches.find(b => b.id === this.currentBranchId);
  }

  /** null/undefined means the main branch (legacy rows). */
  branchName(id?: number | null): string {
    if (id == null) {
      return this.branches.find(b => b.isMain)?.name ?? 'Главный';
    }
    return this.branches.find(b => b.id === id)?.name ?? `Филиал #${id}`;
  }

  async load(force = false): Promise<Branch[]> {
    if (this.loaded && !force) {
      return this.branches;
    }
    const branches = await this.api.list();
    this.branchesSubject.next(branches);
    this.loaded = true;
    if (!this.currentId || !branches.some(b => b.id === this.currentId)) {
      this.currentId = this.mainBranchId;
    }
    return branches;
  }

  /** Public widgets: a branch from the link applies to this page only and is not remembered. */
  pin(branchId: number): void {
    this.currentId = branchId;
  }

  select(branchId: number): void {
    if (branchId === this.currentId) {
      return;
    }
    localStorage.setItem(BranchContext.STORAGE_KEY, String(branchId));
    this.currentId = branchId;
    location.reload();
  }

  param(extra: Record<string, any> = {}): Record<string, any> {
    const id = this.currentBranchId;
    return id != null ? { ...extra, branchId: id } : extra;
  }

  /** An explicit branchId in the body (e.g. from the doctor schedule tab) is kept. */
  withBranch<T extends object>(body: T): T {
    const id = this.currentBranchId;
    if (id == null || (body as { branchId?: number }).branchId != null) {
      return body;
    }
    return { ...body, branchId: id };
  }

  private readStored(): number | undefined {
    const raw = localStorage.getItem(BranchContext.STORAGE_KEY);
    return raw ? +raw : undefined;
  }
}
